/* eslint-disable react-refresh/only-export-components */
import type { ReactNode } from 'react';
import { usePermissions } from '../contexts/PermissionContext';

interface RequirePermissionProps {
  permission?: string;
  anyOf?: string[];
  allOf?: string[];
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Component that conditionally renders children based on user permissions.
 *
 * @example
 * // Single permission
 * <RequirePermission permission="leads.create">
 *   <CreateLeadButton />
 * </RequirePermission>
 *
 * @example
 * // Any of multiple permissions
 * <RequirePermission anyOf={["leads.edit", "leads.delete"]}>
 *   <LeadActions />
 * </RequirePermission>
 *
 * @example
 * // All of multiple permissions
 * <RequirePermission allOf={["leads.view", "leads.export"]}>
 *   <ExportLeadsButton />
 * </RequirePermission>
 *
 * @example
 * // With fallback
 * <RequirePermission permission="admin.settings" fallback={<AccessDenied />}>
 *   <SettingsPanel />
 * </RequirePermission>
 */
export function RequirePermission({
  permission,
  anyOf,
  allOf,
  fallback = null,
  children,
}: RequirePermissionProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyOf && anyOf.length > 0) {
    hasAccess = hasAnyPermission(...anyOf);
  } else if (allOf && allOf.length > 0) {
    hasAccess = hasAllPermissions(...allOf);
  } else {
    // No permission specified, grant access
    hasAccess = true;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
}

/**
 * Higher-order component that wraps a component with permission checking.
 *
 * @example
 * const ProtectedSettings = withPermission(SettingsPage, "settings.view");
 */
export function withPermission<P extends object>(
  Component: React.ComponentType<P>,
  permission: string,
  fallback?: ReactNode
) {
  return function ProtectedComponent(props: P) {
    return (
      <RequirePermission permission={permission} fallback={fallback}>
        <Component {...props} />
      </RequirePermission>
    );
  };
}
